/**
 * Standort der Hundetagesstätte — die eine Quelle fürs Frontend.
 *
 * Angelegt am 26.08.2026, weil dieselbe Adresse als Literal an drei Stellen im
 * Frontend stand (src/components/daycare/NaechsteTageCard.tsx, zweimal in
 * src/pages/DaycareOnboarding.tsx). Beim Umzug oder einer Korrektur an der
 * Schreibweise wäre garantiert eine Kopie stehen geblieben.
 *
 * Backend-Pendant ist DAYCARE_ADDRESS in supabase/functions/_shared/mail.ts.
 * Die beiden lassen sich nicht teilen (Deno gegen Vite), müssen aber wörtlich
 * gleich bleiben — wer hier ändert, ändert dort mit.
 *
 * Ortsangabe ist „Pasing" (Founder-Entscheid B5): so heißt der Standort in den
 * Mails, auf /hundetagesstaette und in allen SEO-Titeln.
 *
 * TODO: NaechsteTageCard.tsx und DaycareOnboarding.tsx auf diese Konstanten
 * umstellen — beide Dateien werden gerade parallel bearbeitet und wurden
 * deshalb hier bewusst nicht angefasst.
 */

/** Straße und Hausnummer — ohne Ort, für strukturierte Daten (schema.org). */
export const DAYCARE_STREET = "Stockacher Str. 5";
export const DAYCARE_POSTAL_CODE = "81243";
export const DAYCARE_CITY = "München";
/** Stadtteil — kein Adressbestandteil, nur Orientierung im Fließtext. */
export const DAYCARE_DISTRICT = "Pasing";

/** Vollständige Adresse in einer Zeile, so wie sie dem Kunden angezeigt wird. */
export const DAYCARE_ADDRESS = `${DAYCARE_STREET}, ${DAYCARE_POSTAL_CODE} ${DAYCARE_CITY} (${DAYCARE_DISTRICT})`;

/** Koordinaten des Gebäudes Stockacher Str. 5 für strukturierte Daten (schema.org `geo`).
 *  Quelle: OpenStreetMap, Gebäude way/27327991, per Nominatim abgefragt am 12.10.2026
 *  (48.1499443, 11.4423326), auf 4 Nachkommastellen (~10 m) gerundet. Die Karte auf
 *  /hundetagesstaette zeigt bewusst weiter nur den Ausschnitt ohne Pin. */
export const DAYCARE_GEO = { latitude: 48.1499, longitude: 11.4423 };

/** Bring- und Abholzeiten, wie sie auf /hundetagesstaette und der Startseite stehen
 *  (Format mit Bindestrich, Copy-Regel 15.09.2026). Eine Quelle auch für /faq und /llms.txt. */
export const DAYCARE_BRINGEN = "07-09 Uhr";
export const DAYCARE_ABHOLEN = "17-19 Uhr";

/** Eröffnungszeitpunkt — Founder-Entscheid 26.08.2026 („Oktober 2026").
 *  Seit 15.10.2026 nicht mehr direkt im Text verwenden, sondern eroeffnungKurz() /
 *  eroeffnungImSatz(): die schalten ab dem 01.11.2026 von selbst auf „geöffnet“ um. */
export const DAYCARE_EROEFFNUNG = "Oktober 2026";

/** Eröffnungstag als Text (Tag der offenen Tür, 31.10.2026). ENTWURF, Freigabe Noah. */
export const DAYCARE_EROEFFNUNG_TAG = "31. Oktober 2026";

/** Ab diesem Berliner Kalendertag gilt die HuTa als geöffnet (Noah 15.10.2026: „Eröffnung
 *  Oktober“ ab 01.11. automatisch ersetzen). */
export const DAYCARE_GEOEFFNET_AB = "2026-11-01";

const BERLIN_TAG = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit",
});

/** true ab dem 01.11.2026 (Berliner Zeit). Rein und ohne Browser-API, läuft auch beim Build. */
export function hutaGeoeffnet(jetzt: Date = new Date()): boolean {
  return BERLIN_TAG.format(jetzt) >= DAYCARE_GEOEFFNET_AB;
}

/** Für Badges und Eyebrows: „Eröffnung 31. Oktober 2026“, ab 01.11. „Jetzt geöffnet“. ENTWURF. */
export function eroeffnungKurz(jetzt: Date = new Date()): string {
  return hutaGeoeffnet(jetzt) ? "Jetzt geöffnet" : `Eröffnung ${DAYCARE_EROEFFNUNG_TAG}`;
}

/** Im Satz/in Klammern: „Eröffnung 31. Oktober 2026“, ab 01.11. „geöffnet seit 31. Oktober 2026“. ENTWURF. */
export function eroeffnungImSatz(jetzt: Date = new Date()): string {
  return hutaGeoeffnet(jetzt) ? `geöffnet seit ${DAYCARE_EROEFFNUNG_TAG}` : `Eröffnung ${DAYCARE_EROEFFNUNG_TAG}`;
}

/** Kartenlink. Query statt Koordinaten: Google löst die Adresse selbst auf,
 *  damit steht in der Karte auch nach einem Hausnummern-Zusatz das Richtige. */
export const DAYCARE_MAPS_URL = "https://maps.google.com/?q=Stockacher+Str.+5,+81243+M%C3%BCnchen";
